import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { BRAND_LOGO_URL } from '../data/brand';

const Auth = ({ onAuth }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    age: '',
    profile_type: 'youth'
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error(t('auth.fillFields'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });
      toast.success(t('auth.welcomeBack'));
      onAuth(response.data.access_token, response.data.user);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('auth.loginFailed');
      if (error.response?.status === 401) toast.error(t('auth.invalidCreds'));
      else toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.name) {
      toast.error(t('auth.fillRequired'));
      return;
    }
    if (formData.password.length < 6) {
      toast.error(t('auth.passwordShort'));
      return;
    }
    setIsLoading(true);
    try {
      const registerData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        profile_type: formData.profile_type
      };
      if (formData.age) registerData.age = parseInt(formData.age);

      const response = await axios.post('/auth/register', registerData);
      toast.success(t('auth.welcomeNew', { name: formData.name }));
      onAuth(response.data.access_token, response.data.user);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('auth.registerFailed');
      if (error.response?.status === 400 && errorMessage.includes('already registered')) {
        toast.error(t('auth.emailExists'));
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
        style={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxuYXR1cmUlMjBoZWFsaW5nfGVufDB8fHx8MTc1OTU4NzU0Nnww&ixlib=rb-4.1.0&q=85)'
        }}
      />

      <div className="relative z-10 max-w-md w-full">
        <Card className="glass-card p-8" data-testid="auth-card">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white shadow-xl mx-auto mb-4 ring-4 ring-purple-100">
              <img src={BRAND_LOGO_URL} alt="The Brain Superheroes" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-2xl font-extrabold text-gray-900 mb-1">{t('brand.name')}</h1>
            <p className="text-sm text-purple-600 font-semibold italic">{t('brand.tagline')}</p>
            <p className="text-gray-600 mt-3 text-sm">{t('auth.subtitle')}</p>
          </div>

          {/* Google / Admin login */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full mb-4 flex items-center justify-center gap-2"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 13.9-5.5l-6.4-5.3c-2 1.5-4.6 2.4-7.5 2.4-5.4 0-9.9-3.5-11.5-8.4l-6.6 5.1C9.6 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.4 5.3C42.7 36.4 44 30.6 44 24c0-1.2-.1-2.3-.4-3.5z"/>
            </svg>
            {t('auth.googleLogin')}
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">{t('auth.orDivider')}</span>
            </div>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">{t('auth.register')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">{t('common.email')}</Label>
                  <Input id="email" name="email" type="email" required className="custom-input" placeholder="your@email.com" value={formData.email} onChange={handleInputChange} data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="password">{t('common.password')}</Label>
                  <Input id="password" name="password" type="password" required className="custom-input" placeholder="••••••••" value={formData.password} onChange={handleInputChange} data-testid="login-password-input" />
                </div>
                <Button type="submit" disabled={isLoading} className="btn-primary w-full" data-testid="login-submit-btn">
                  {isLoading ? t('auth.signingIn') : t('auth.signIn')}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">{t('auth.fullName')}</Label>
                  <Input id="register-name" name="name" type="text" required className="custom-input" placeholder={t('auth.yourName')} value={formData.name} onChange={handleInputChange} data-testid="register-name-input" />
                </div>
                <div>
                  <Label htmlFor="register-email">{t('common.email')}</Label>
                  <Input id="register-email" name="email" type="email" required className="custom-input" placeholder="your@email.com" value={formData.email} onChange={handleInputChange} data-testid="register-email-input" />
                </div>
                <div>
                  <Label htmlFor="register-password">{t('common.password')}</Label>
                  <Input id="register-password" name="password" type="password" required className="custom-input" placeholder="••••••••" value={formData.password} onChange={handleInputChange} data-testid="register-password-input" />
                </div>
                <div>
                  <Label htmlFor="age">{t('auth.age')}</Label>
                  <Input id="age" name="age" type="number" min="12" max="100" className="custom-input" placeholder={t('auth.ageHint')} value={formData.age} onChange={handleInputChange} data-testid="register-age-input" />
                </div>
                <div>
                  <Label htmlFor="profile_type">{t('auth.profileType')}</Label>
                  <select id="profile_type" name="profile_type" className="custom-input w-full" value={formData.profile_type} onChange={handleInputChange} data-testid="register-profile-select">
                    <option value="youth">{t('auth.profileYouth')}</option>
                    <option value="parent">{t('auth.profileParent')}</option>
                    <option value="family">{t('auth.profileFamily')}</option>
                  </select>
                </div>
                <Button type="submit" disabled={isLoading} className="btn-primary w-full" data-testid="register-submit-btn">
                  {isLoading ? t('auth.creating') : t('auth.createAccount')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>{t('auth.consent')}</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
